import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { getCurrencyConfig } from '../components/atoms/FormattedCurrency';
import { showNotification } from '../components/molecules/Notifications';
import additionalPaymentConfig from '../components/molecules/PaymentOptions/additionalPaymentConfig';
import camelCase from './camelCase';
import { Notification } from './checkoutState';
import { config } from './config';
import { pushErrorTrackingEventFromI18nKey } from './tracking';

const useGenericNotifications = () => {
  const { formatMessage, formatDisplayName, formatNumber } = useIntl();
  const isInitialNotifications = useRef(true);
  const lastNotificationKey = useRef<string>();

  const getTranslatedPaymentMethod = (paymentOptionKey: string) => {
    const i18nKey = additionalPaymentConfig[paymentOptionKey]?.i18nKey ?? camelCase(paymentOptionKey);
    return formatMessage({ id: `payment.paymentMethod.options.${i18nKey}` });
  };

  const formatReplacements = (replacements: Record<string, string>) => {
    const { paymentOptionKey, paymentOptionKeys, date, orderValue, countryCode, countryCodes } = replacements;
    return {
      ...replacements,
      ...(paymentOptionKey && {
        paymentMethodName: getTranslatedPaymentMethod(paymentOptionKey)
      }),
      ...(paymentOptionKeys && {
        paymentMethodNames: paymentOptionKeys
          .replace(/\s/g, '')
          .split(',')
          .map(item => getTranslatedPaymentMethod(item))
          .join(', ')
      }),
      ...(date && {
        date: new Date(date)
      }),
      ...(orderValue && {
        orderValue: formatNumber(parseInt(orderValue) / 100, getCurrencyConfig())
      }),
      ...(countryCode && {
        countryName: formatDisplayName(countryCode, { type: 'region' })
      }),
      ...(countryCodes && {
        countryNames: countryCodes
          .replace(/\s/g, '')
          .split(',')
          .map(code => formatDisplayName(code, { type: 'region' }))
          .join(', ')
      })
    };
  };

  const triggerGenericNotifications = (notifications?: Notification[]) => {
    notifications?.forEach(({ type, text, textTranslationKey, replacements = {} }) => {
      const voucherSuccessNotifications = [
        'payment.voucher.notifications.success',
        'payment.voucher.notifications.successToPromotions',
        'payment.voucher.notifications.removalSuccess'
      ];

      const shouldSkipVoucherNotification =
        textTranslationKey &&
        voucherSuccessNotifications.includes(textTranslationKey) &&
        textTranslationKey === lastNotificationKey.current;

      const isInitialVoucherNotificationDisabled =
        config.features.vouchers.disableInitialVoucherNotification &&
        textTranslationKey === 'payment.voucher.notifications.success' &&
        isInitialNotifications.current;

      if (isInitialVoucherNotificationDisabled || shouldSkipVoucherNotification) {
        return;
      }

      lastNotificationKey.current = textTranslationKey!;
      const intlReplacements = formatReplacements(replacements);
      setTimeout(
        () =>
          showNotification({
            level: type,
            text: text ?? formatMessage({ id: textTranslationKey }, { ...intlReplacements }),
            autoClose: true
          }),
        0
      );
      if (type === 'error') {
        pushErrorTrackingEventFromI18nKey(textTranslationKey);
      }
    });

    isInitialNotifications.current = false;
  };

  return { triggerGenericNotifications };
};

export default useGenericNotifications;
